import { format } from 'date-fns';
import { playEventDisplayTheme } from './playEventDisplayTheme';

interface PlayEventDisplayClockProps {
  now: Date;
  eventLive?: boolean;
  eventFinished?: boolean;
  /** When the event is in its final minutes, the live dot turns amber. */
  eventEndingSoon?: boolean;
}

export function PlayEventDisplayClock({
  now,
  eventLive = false,
  eventFinished = false,
  eventEndingSoon = false,
}: PlayEventDisplayClockProps) {
  const showLiveIndicator = eventLive || eventFinished;
  const liveIndicatorLabel = eventFinished
    ? 'Event finished'
    : eventEndingSoon
      ? 'Event ending soon'
      : 'Event live';
  const pingClassName = eventEndingSoon
    ? 'absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75 dark:bg-amber-400'
    : 'absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75 dark:bg-emerald-400';
  const dotClassName = eventEndingSoon
    ? 'relative inline-flex h-3 w-3 rounded-full bg-amber-500 dark:bg-amber-400'
    : 'relative inline-flex h-3 w-3 rounded-full bg-emerald-500 dark:bg-emerald-400';
  const statusLabelClassName = eventEndingSoon
    ? 'text-xs font-medium text-amber-600 dark:text-amber-400'
    : playEventDisplayTheme.mutedSubtle;

  return (
    <div className="text-left sm:text-right">
      <p className={statusLabelClassName}>{eventEndingSoon ? 'Ending soon' : 'Now'}</p>
      <div className="mt-0.5 flex items-center gap-3 sm:justify-end">
        {showLiveIndicator ? (
          <span
            className="relative flex h-3 w-3 shrink-0"
            role="status"
            aria-label={liveIndicatorLabel}
          >
            {eventFinished ? (
              <span className="inline-flex h-3 w-3 rounded-full bg-red-500" aria-hidden />
            ) : (
              <>
                <span className={pingClassName} aria-hidden />
                <span className={dotClassName} aria-hidden />
              </>
            )}
          </span>
        ) : null}
        <time
          className="block text-4xl font-mono tabular-nums leading-none sm:text-5xl"
          dateTime={now.toISOString()}
        >
          {format(now, 'HH:mm')}
        </time>
      </div>
      <p className={`mt-1 ${playEventDisplayTheme.mutedFaint}`}>{format(now, 'EEE d MMM')}</p>
    </div>
  );
}
